import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Check, Shield, Clock, Award, Users, ArrowRight } from 'lucide-react';
import { pricingPlans } from '../mock';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import countryCodes from '../data/countryCodes';
import { toast } from 'sonner';
import InquiryModal from '../components/InquiryModal';
import CountryCodeSelect from '../components/CountryCodeSelect';
import DecorativeImage from '../components/DecorativeImage';

const serviceContent = {
  trademark: {
    title: 'Trademark Registration Services',
    subtitle: 'Protect Your Brand Identity with Federal Trademark Registration',
    description: 'Secure exclusive rights to your Trademark Application Hub, logo, and slogan with comprehensive trademark registration. Our licensed attorneys guide you through every stage of the USPTO filing process.',
    benefits: ['Nationwide exclusive rights to your brand', 'Legal presumption of ownership', 'Ability to use the registered symbol', 'Enhanced protection against infringement', 'Increased brand value and credibility', 'Amazon Brand Registry eligibility'],
    process: ['Comprehensive trademark search', 'Application preparation and filing', 'USPTO examination support', 'Office action responses', 'Registration certificate delivery']
  },
  copyright: {
    title: 'Copyright Registration Services',
    subtitle: 'Protect Your Creative Works with Federal Copyright Registration',
    description: 'Safeguard your original creative works including books, music, art, software, and more. Our copyright registration services provide comprehensive legal protection for your intellectual creations.',
    benefits: ['Public record of your copyright claim', 'Legal evidence of ownership', 'Ability to sue for infringement', 'Statutory damages eligibility', 'Attorney fees recovery rights', 'International protection through treaties'],
    process: ['Work identification and classification', 'Application preparation', 'Copyright Office filing', 'Examination monitoring', 'Certificate of registration']
  },
  patent: {
    title: 'Patent Services',
    subtitle: 'Protect Your Inventions with Comprehensive Patent Services',
    description: 'Secure patent protection for your innovations. From utility patents to design patents, we help inventors protect their groundbreaking ideas with precision and expertise.',
    benefits: ['Exclusive rights to your invention', 'Prevent others from making, using, or selling', 'Increased business valuation', 'Licensing and monetization opportunities', 'Competitive market advantage', '20-year protection period'],
    process: ['Prior art search and analysis', 'Patentability assessment', 'Patent application drafting', 'USPTO prosecution', 'Patent grant and maintenance']
  },
  'company-formation': {
    title: 'Company Formation Services',
    subtitle: 'Establish Your Business with Proper Legal Structure',
    description: 'Start your business on solid legal footing. We help you choose and establish the right business structure — LLC, C-Corp, S-Corp, or Non-Profit — tailored to your commercial objectives.',
    benefits: ['Limited liability protection', 'Tax optimization opportunities', 'Enhanced business credibility', 'Easier access to funding', 'Perpetual existence', 'Professional business structure'],
    process: ['Business structure consultation', 'Name availability check', 'Articles of incorporation/organization', 'EIN acquisition', 'Operating agreement/bylaws']
  }
};

const ServicePage = () => {
  const { serviceType } = useParams();
  const content = serviceContent[serviceType] || serviceContent.trademark;
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');

  useEffect(() => {
    const fetchGeo = async () => {
      try {
        const info = await getCountryInfo();
        if (info) { setDetectedCountry(info); const match = countryCodes.find(c => c.country === info.countryCode); if (match) setCountryCode(match.code); }
      } catch (e) { console.error('Geo fetch error:', e); }
    };
    fetchGeo();
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const handlePhoneChange = (e) => { const v = e.target.value.replace(/[^0-9\s\-]/g, ''); const d = v.replace(/\D/g, ''); if (d.length > 10) return; setFormData(prev => ({ ...prev, phone: v })); if (errors.phone && d.length === 10) setErrors(prev => ({ ...prev, phone: '' })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false; const newErrors = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { newErrors.email = 'Please provide a valid email'; hasErrors = true; }
    const digits = formData.phone.replace(/\D/g, '');
    if (digits.length !== 10) { newErrors.phone = 'Enter a 10-digit phone number'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); toast.error('Please correct the highlighted fields'); return; }
    setIsLoading(true);
    try {
      const leadData = { name: formData.name, email: formData.email, phone: formData.phone, countryCode, country: countryCodes.find(c => c.code === countryCode)?.country || 'US', description: formData.message || `Service inquiry: ${content.title}` };
      const result = await submitLeadWithGeoLocation(leadData, null, `Service Page: ${content.title}`);
      if (result.success) { toast.success(result.message || 'Request submitted.'); setFormData({ name: '', email: '', phone: '', message: '' }); setErrors({ email: '', phone: '' }); }
      else { toast.error(result.message || 'Submission failed.'); }
    } catch (error) { toast.error('Failed to submit.'); } finally { setIsLoading(false); }
  };

  const whyItems = [
    { icon: Shield, title: 'Expert Attorneys', desc: 'Licensed IP attorneys with decades of practice' },
    { icon: Clock, title: 'Fast Processing', desc: 'Expedited turnaround with priority filing options' },
    { icon: Award, title: 'Proven Results', desc: 'Industry-leading approval and registration rates' },
    { icon: Users, title: 'Dedicated Support', desc: 'Personal counsel throughout the entire engagement' }
  ];

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="relative bg-[#1A3628] py-20 overflow-hidden" data-testid="sp-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1641415171273-9dd67f6ebeee?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)` }} />
        <div className="absolute inset-0 bg-[#1A3628]/92" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <p className="eyebrow text-[#C85A40] mb-4">{content.subtitle}</p>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-6 tracking-tighter">{content.title}</h1>
          <p className="font-body text-base text-white/70 max-w-3xl mx-auto">{content.description}</p>
        </div>
      </section>

      {/* Mobile Form */}
      <section className="md:hidden py-10 bg-[#EAE7DF]" data-testid="sp-mobile-contact-form">
        <div className="max-w-lg mx-auto px-4">
          <p className="eyebrow text-[#C85A40] mb-2 text-center">Begin Your Filing</p>
          <h2 className="font-display text-2xl font-bold text-[#111C16] text-center mb-6">Get Started Today</h2>
          <form onSubmit={handleSubmit} className="bg-white p-6 space-y-4 border border-[#D5D1C8]">
            <Input type="text" placeholder="Full Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="rounded-3xl border-[#D5D1C8] font-body" data-testid="sp-mobile-name" />
            <div><Input type="email" placeholder="Email Address" value={formData.email} onChange={(e) => { setFormData({...formData, email: e.target.value}); if (errors.email && validateEmail(e.target.value)) setErrors(prev => ({...prev, email: ''})); }} required className="rounded-3xl border-[#D5D1C8] font-body" data-testid="sp-mobile-email" />{errors.email && <p className="text-red-500 text-xs mt-1 font-body">{errors.email}</p>}</div>
            <div><div className="flex gap-2"><select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="w-24 px-2 py-2 border border-[#D5D1C8] rounded-3xl text-sm bg-white font-body" data-testid="sp-mobile-country">{countryCodes.map((c) => (<option key={c.code + c.country} value={c.code}>{c.code} {c.country}</option>))}</select><Input type="tel" placeholder="Phone Number" value={formData.phone} onChange={handlePhoneChange} required className="flex-1 rounded-3xl border-[#D5D1C8] font-body" data-testid="sp-mobile-phone" /></div>{detectedCountry && <p className="font-body text-xs text-[#4A5D53/60] mt-1">Detected: {detectedCountry.countryName}</p>}{errors.phone && <p className="text-red-500 text-xs mt-1 font-body">{errors.phone}</p>}</div>
            <Textarea placeholder="What would you like to protect?" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required rows={3} className="rounded-3xl border-[#D5D1C8] font-body" data-testid="sp-mobile-message" />
            <Button type="submit" disabled={isLoading} className="w-full bg-[#C85A40] hover:bg-[#A64730] text-[#111C16] btn-hover" data-testid="sp-mobile-submit">{isLoading ? 'Processing...' : 'Request Free Consultation'}</Button>
          </form>
        </div>
      </section>

      {/* Benefits & Process */}
      <section className="py-24 bg-white" data-testid="sp-benefits">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="eyebrow text-[#C85A40] mb-4">Advantages</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[#111C16] mb-8 tracking-tighter">Key Benefits</h2>
              <ul className="space-y-4">
                {content.benefits.map((b, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <Check className="h-5 w-5 text-[#C85A40] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="font-body text-sm text-[#4A5D53]">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="eyebrow text-[#C85A40] mb-4">The Process</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[#111C16] mb-8 tracking-tighter">Our Process</h2>
              <div className="space-y-6">
                {content.process.map((step, i) => (
                  <div key={i} className="flex items-start space-x-4 group cursor-pointer p-4 rounded-2xl transition-all duration-300 hover:bg-[#F9F8F6] hover:shadow-md hover:-translate-x-1">
                    <div className="w-11 h-11 rounded-xl bg-[#C85A40] flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <span className="font-display text-sm font-bold text-white">{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <p className="font-body text-sm text-[#4A5D53] mt-2.5 transition-colors duration-300 group-hover:text-[#111C16]">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white" data-testid="sp-why-choose">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — Content */}
            <div>
              <p className="eyebrow text-[#C85A40] mb-4">Why Choose Us</p>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[#111C16] tracking-tighter leading-tight mb-6">
                Why Businesses Trust Our {content.title.split(' ')[0]} Service
              </h2>
              <p className="font-body text-base text-[#4A5D53] leading-relaxed mb-8">
                Licensed IP attorneys with decades of experience. Industry-leading approval rates, expedited filing, and dedicated support throughout the entire engagement. We handle everything so you can focus on growing your business.
              </p>
              <button
                onClick={() => { setButtonContext(`Why Choose Us - ${content.title}`); setIsModalOpen(true); }}
                className="bg-[#C85A40] hover:bg-[#A64730] text-white font-body font-medium px-8 py-4 rounded-full transition-colors flex items-center gap-2"
                data-testid="sp-why-choose-cta"
              >
                Schedule Consultation
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            {/* Right — Image */}
            <div>
              <DecorativeImage
                src="https://images.unsplash.com/photo-1758873268394-443e1ac80032?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85"
                alt="Professional legal office"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-white" data-testid="sp-pricing">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="eyebrow text-[#C85A40] mb-4">Investment</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[#111C16] tracking-tighter">Transparent Pricing</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            {pricingPlans.map((plan) => (
              <div key={plan.id} className={`p-6 border border-[#D5D1C8] hover-lift hover-gold-border ${plan.popular ? 'border-[#C85A40] bg-[#1A3628]' : 'bg-white'}`}>
                {plan.popular && <div className="bg-[#C85A40] text-[#111C16] text-xs font-body font-semibold px-4 py-1 text-center uppercase tracking-wider mb-4">Recommended</div>}
                <h3 className={`font-display text-xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-[#111C16]'}`}>{plan.name}</h3>
                <div className="mb-4"><span className={`font-display text-3xl font-bold ${plan.popular ? 'text-[#C85A40]' : 'text-[#C85A40]'}`}>${plan.price}</span><p className={`font-body text-xs mt-1 ${plan.popular ? 'text-white/60' : 'text-[#4A5D53/60]'}`}>+ Federal Filing Fee</p></div>
                <p className={`font-body text-sm mb-4 ${plan.popular ? 'text-white/70' : 'text-[#4A5D53]'}`}>{plan.description}</p>
                <Button className={`w-full btn-hover ${plan.popular ? 'bg-[#C85A40] hover:bg-[#A64730] text-[#111C16]' : 'bg-[#1A3628] hover:bg-[#11241A] text-white'}`}>Register Your Trademark</Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Desktop Form */}
      <section className="hidden md:block py-24 bg-[#1A3628]" data-testid="sp-desktop-form">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-8">
            <p className="eyebrow text-[#C85A40] mb-4">Begin Your Filing</p>
            <h2 className="font-display text-3xl font-bold text-white mb-2">Get Started Today</h2>
            <p className="font-body text-sm text-white/60">Contact us for a complimentary consultation</p>
          </div>
          <form onSubmit={handleSubmit} className="bg-white p-8 space-y-4 border border-[#D5D1C8]" data-testid="sp-desktop-form-el">
            <Input type="text" placeholder="Full Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="rounded-3xl border-[#D5D1C8] font-body" data-testid="sp-form-name" />
            <div><Input type="email" placeholder="Email Address" value={formData.email} onChange={(e) => { setFormData({...formData, email: e.target.value}); if (errors.email && validateEmail(e.target.value)) setErrors(prev => ({...prev, email: ''})); }} required className="rounded-3xl border-[#D5D1C8] font-body" data-testid="sp-form-email" />{errors.email && <p className="text-red-500 text-xs mt-1 font-body">{errors.email}</p>}</div>
            <div><div className="flex gap-2"><CountryCodeSelect value={countryCode} onChange={setCountryCode} countryCodes={countryCodes} className="w-24" testId="sp-form-country" /><Input type="tel" placeholder="Phone Number" value={formData.phone} onChange={handlePhoneChange} required className="flex-1 rounded-3xl border-[#D5D1C8] font-body" data-testid="sp-form-phone" /></div>{detectedCountry && <p className="font-body text-xs text-[#4A5D53/60] mt-1">Detected: {detectedCountry.countryName}</p>}{errors.phone && <p className="text-red-500 text-xs mt-1 font-body">{errors.phone}</p>}</div>
            <Textarea placeholder="What would you like to protect?" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required rows={4} className="rounded-3xl border-[#D5D1C8] font-body" data-testid="sp-form-message" />
            <Button type="submit" disabled={isLoading} className="w-full bg-[#C85A40] hover:bg-[#A64730] text-[#111C16] btn-hover">{isLoading ? 'Processing...' : 'Request Free Consultation'}</Button>
          </form>
        </div>
      </section>
      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={buttonContext} />
    </div>
  );
};

export default ServicePage;
